import React, { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useLazyQuery } from '@apollo/client'
import { Container, Grid, Alert, Snackbar } from '@mui/material'
import AsyncSelect from 'react-select/async'
import SectionAccordion from 'gui/components/accordion/section-accordion'
import MuiButton from 'gui/components/common/MuiButton'
import { CONTENT_REFERENCE_TYPE_PRIMARY_COURSE } from 'services/content-references/types'
import { CONTENT_TYPE_COURSE } from 'services/content/content-types'
import {
  CHECK_VIDEO_COURSE_MODULE_LINK,
  GET_VIDEO_PRIMARY_COURSE,
  CONTENT_LIST_QUERY,
} from './queries'
import { CREATE_CONTENT_REFERENCE, DELETE_CONTENT_REFERENCE } from './content-references/queries'
import type { SnackbarState } from './types'
import './primary-course-section.scss'

interface PrimaryCourseSectionProps {
  videoId: string
  videoLanguage: string
  videoContentId: number
  videoContentType: string
  disabled: boolean
  mainFormIsDirty: boolean
}

interface CourseOption {
  label: string
  value: number
}

function PrimaryCourseSection (props: PrimaryCourseSectionProps) {
  const { videoId, videoLanguage, videoContentId, videoContentType, disabled, mainFormIsDirty } =
    props

  const [selectedCourse, setSelectedCourse] = useState<CourseOption | null>(null)
  const [initialCourse, setInitialCourse] = useState<CourseOption | null>(null)
  const [existingReferenceId, setExistingReferenceId] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  })

  const { data: linkData, loading: linkLoading } = useQuery(CHECK_VIDEO_COURSE_MODULE_LINK, {
    variables: { videoId },
    skip: !videoId,
  })

  const { data: primaryData, loading: primaryLoading } = useQuery(GET_VIDEO_PRIMARY_COURSE, {
    variables: { videoId },
    skip: !videoId,
  })

  const [loadCourses] = useLazyQuery(CONTENT_LIST_QUERY)
  const [createReference] = useMutation(CREATE_CONTENT_REFERENCE)
  const [deleteReference] = useMutation(DELETE_CONTENT_REFERENCE)

  const isLinkedToCourseModule = useMemo(() => {
    const references = linkData?.contentList?.content?.[0]?.contentReferencesFrom
    return references && references.length > 0
  }, [linkData])

  useEffect(() => {
    const primaryRefs = primaryData?.contentList?.content?.[0]?.primaryCourse
    if (primaryRefs && primaryRefs.length > 0) {
      const ref = primaryRefs[0]
      const course = ref.contentTo?.[0]
      if (course) {
        const option = {
          label: course.title,
          value: course.contentId,
        }
        setSelectedCourse(option)
        setInitialCourse(option)
        setExistingReferenceId(ref.id)
      } else {
        // Reset if course data is missing
        setSelectedCourse(null)
        setInitialCourse(null)
        setExistingReferenceId(null)
      }
    } else {
      // Reset if no primary course reference exists
      setSelectedCourse(null)
      setInitialCourse(null)
      setExistingReferenceId(null)
    }
  }, [primaryData])

  const loadCourseOptions = async (searchTerm: string): Promise<CourseOption[]> => {
    try {
      const { data } = await loadCourses({
        variables: {
          limit: 50,
          order: [['title', 'asc']],
          where: {
            contentType: { eq: CONTENT_TYPE_COURSE },
            ...(searchTerm && { title: { ilike: `%${searchTerm}%` } }),
          },
          language: [videoLanguage, '_primary'],
        },
      })

      return (
        data?.contentList?.content?.map((course: { title: string; contentId: number }) => ({
          label: course.title,
          value: course.contentId,
        })) || []
      )
    } catch (err) {
      // eslint-disable-next-line no-console -- keep for in-depth error visibility when loading courses fails
      console.error('Error loading courses:', err)
      return []
    }
  }

  const handleCourseChange = (option: CourseOption | null) => {
    setSelectedCourse(option)
  }

  const isDirty = selectedCourse?.value !== initialCourse?.value

  const handleSave = async () => {
    if (mainFormIsDirty) {
      setSnackbar({
        open: true,
        message: 'Please save or discard main form changes before updating Primary Course',
        severity: 'warning',
      })
      return
    }

    setIsSaving(true)

    try {
      // Delete existing reference if there is one and selection changed
      if (existingReferenceId && isDirty) {
        await deleteReference({
          variables: {
            contentReference: {
              id: existingReferenceId,
            },
          },
        })
        setExistingReferenceId(null)
      }

      // Create new reference if a course is selected
      if (selectedCourse && isDirty) {
        await createReference({
          variables: {
            contentReference: {
              fromContentContentId: videoContentId,
              fromContentContentType: videoContentType,
              toContentContentId: selectedCourse.value,
              toContentContentType: CONTENT_TYPE_COURSE,
              type: CONTENT_REFERENCE_TYPE_PRIMARY_COURSE,
            },
          },
        })
      }

      setSnackbar({
        open: true,
        message: 'Primary Course saved! Refreshing page in 1 second...',
        severity: 'success',
      })

      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setSnackbar({
        open: true,
        message: `Error saving Primary Course: ${errorMessage}`,
        severity: 'error',
      })
      setIsSaving(false)
    }
  }

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  if (linkLoading || primaryLoading) {
    return null
  }

  if (!isLinkedToCourseModule) {
    return null
  }

  return (
    <Container className="content-detail__section primary-course-section" maxWidth={false}>
      <SectionAccordion title="Primary Course" collapsed={false}>
        <Alert severity="info" sx={{ mb: 3 }}>
          This video is part of a course. By picking a &quot;Primary Course&quot; for this piece of
          content, the video will link to this course in specific scenarios on the Front End.
          <br />
          This section saves independently and will refresh the page after saving.
          {mainFormIsDirty && <strong> Please save or discard your form changes first.</strong>}
        </Alert>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={2}>
            <p>Primary Course</p>
          </Grid>
          <Grid item xs={10}>
            <AsyncSelect
              value={selectedCourse}
              loadOptions={loadCourseOptions}
              onChange={handleCourseChange}
              placeholder="Search for a course..."
              isClearable
              isDisabled={disabled || isSaving || mainFormIsDirty}
              cacheOptions
              defaultOptions
              className="primary-course-section__select"
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <Grid item xs={2}></Grid>
          <Grid item xs={10}>
            <MuiButton
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!isDirty || isSaving || disabled || mainFormIsDirty}
              title={mainFormIsDirty ? 'Save main form first' : ''}
              className="primary-course-section__save-button"
            >
              {isSaving ? 'Saving & Refreshing...' : 'Save Primary Course'}
            </MuiButton>
          </Grid>
        </Grid>
      </SectionAccordion>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default PrimaryCourseSection
